import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * PermissionGuard Component - Conditionally renders children based on user permissions
 * @param {Object} props
 * @param {string|string[]} props.permission - Required permission(s) to render children
 * @param {'any'|'all'} props.mode - 'any' requires any permission, 'all' requires all permissions
 * @param {React.ReactNode} props.children - Children to render if permissions are satisfied
 * @param {React.ReactNode} props.fallback - Optional fallback to render if permissions are not satisfied
 * @param {Function} props.canCreate - Shortcut for create permission check
 * @param {Function} props.canRead - Shortcut for read permission check
 * @param {Function} props.canUpdate - Shortcut for update permission check
 * @param {Function} props.canDelete - Shortcut for delete permission check
 * @param {Function} props.canApprove - Shortcut for approve permission check
 */
export const PermissionGuard = ({ 
  permission, 
  mode = 'any', 
  children, 
  fallback = null,
  module,
  action
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, canCreate, canRead, canUpdate, canDelete, canApprove } = usePermissions();

  let hasRequiredPermission = false;

  // Handle module/action shortcut
  if (module && action) {
    switch (action.toLowerCase()) {
      case 'create':
        hasRequiredPermission = canCreate(module);
        break;
      case 'read':
      case 'view':
        hasRequiredPermission = canRead(module);
        break;
      case 'update':
      case 'edit':
        hasRequiredPermission = canUpdate(module);
        break;
      case 'delete':
        hasRequiredPermission = canDelete(module);
        break;
      case 'approve':
        hasRequiredPermission = canApprove(module);
        break;
      default:
        hasRequiredPermission = hasPermission(`${module}:${action}`);
    }
  }
  // Handle direct permission checks
  else if (Array.isArray(permission)) {
    hasRequiredPermission = mode === 'all' 
      ? hasAllPermissions(permission)
      : hasAnyPermission(permission);
  } else if (typeof permission === 'string') {
    hasRequiredPermission = hasPermission(permission);
  }

  return hasRequiredPermission ? children : fallback;
};

/**
 * withPermission HOC - Wraps a component with permission checking
 * @param {string|string[]} permission - Required permission(s)
 * @param {'any'|'all'} mode - Permission checking mode
 * @param {React.ReactNode} fallback - Optional fallback component
 */
export const withPermission = (permission, mode = 'any', fallback = null) => {
  return (WrappedComponent) => {
    return (props) => (
      <PermissionGuard permission={permission} mode={mode} fallback={fallback}>
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
};

/**
 * Permission-based button component
 */
export const PermissionButton = ({ 
  permission, 
  module, 
  action, 
  mode = 'any',
  children, 
  fallback = null,
  ...props 
}) => {
  return (
    <PermissionGuard 
      permission={permission} 
      module={module} 
      action={action} 
      mode={mode} 
      fallback={fallback}
    >
      <button {...props}>{children}</button>
    </PermissionGuard>
  );
};

export default PermissionGuard;
